import { Fragment, useEffect, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { changeAmount, formatBudgetPeriod, previousKey, rowRef, sameProject, sameRow, sheetTotal, UNBUDGETED_LABEL, type AttentionKind, type BudgetRowRef, type AttentionRow, type BudgetRow, type PreviousFigures, type ProjectRef, type ProjectRow, type UnbudgetedReason, type UnitSheet, type YearEndLine } from "@po/shared";
import { download, qs, type BudgetsPage } from "../lib/api";
import { useDashboard } from "../pages/Dashboard";
import type { BudgetLineStep, ProjectStep } from "../components/Drawer";
import { autoCountKey, isZeroLine, previousFigures, sheetFigures, sumLines, sumPrevious, useAutoCountSheet, type AutoCountLine, type AutoCountVessel } from "../components/AutoCountBudget";
import { sameAutoCountLine, type AutoCountLineRef } from "../components/AutoCountLineDrawer";
import {
  BUDGET_CAPTION,
  BudgetsAsOf,
  ChangeMark,
  ClosedYear,
  ForecastToggle,
  changeKey,
  indexChanges,
  OverChip,
  OverCount,
  PeriodPicker,
  SERTICA_COMMENTS,
  SerticaPath,
  signed,
  usd,
  useBudgetsPage,
  UsedBar,
  windowLabel,
  type LineChanges,
} from "../components/budget";

/** The text a user without budget access sees on the tab (SPEC §8.6). */
export const LOCKED_MESSAGE =
  "You need the ProcurementBudget right in Sertica to see budgets. Ask your Sertica admin, then sign in again.";

type Pane = { kind: "all" } | { kind: "unit"; unitNo: string | null } | { kind: "autocount"; key: string } | { kind: "projects" } | { kind: "unbudgeted" } | { kind: "changes" };
const paneKey = (p: Pane) => (p.kind === "unit" ? `unit:${p.unitNo ?? ""}` : p.kind === "autocount" ? `autocount:${p.key}` : p.kind);

type Figures = { budgetUsd: number | null; committedUsd: number; accruedUsd: number; otherCostUsd?: number };
const vesselTotal = sheetTotal;

/**
 * Opens the line drawer for a row of a vessel's sheet in a period (SPEC §8.6), with the rows of the
 * list it was opened from for Previous and Next.
 */
function useOpenLine() {
  const { open } = useDashboard();
  return (row: BudgetRowRef | null, period: BudgetsPage["period"], steps?: BudgetLineStep[]) =>
    row && open({ type: "budget-line", row, period: formatBudgetPeriod(period), steps });
}

const stepOf = (row: BudgetRowRef | null, period: BudgetsPage["period"]): BudgetLineStep[] => (row ? [{ row, period: formatBudgetPeriod(period) }] : []);

/** Whether a row is the one the line drawer shows, to highlight it. */
function useShown() {
  const { drawer } = useDashboard();
  return (row: BudgetRowRef | null, period: BudgetsPage["period"]) =>
    !!row && drawer?.type === "budget-line" && drawer.period === formatBudgetPeriod(period) && sameRow(drawer.row, row);
}

/** The props that make a table row open the line drawer; its label button is the keyboard target. */
const opens = (go: () => void, shown = false, extra = "") => ({ className: ["opens", extra, shown ? "selected" : ""].filter(Boolean).join(" "), onClick: go, "aria-current": shown || undefined });

const vesselName = (u: { unitName: string | null; unitNo: string | null }) => u.unitName ?? u.unitNo ?? "No vessel";
/** Spend: committed + accrued, shown as one figure. */
const spendOf = (f: { committedUsd: number; accruedUsd: number }) => f.committedUsd + f.accruedUsd;
const usedShare = (f: Figures) => (f.budgetUsd ? spendOf(f) / f.budgetUsd : spendOf(f) > 0 ? Infinity : 0);

type Total = Figures & { remainingUsd: number | null };
/** A vessel's Sertica and SEA or PEA figures added up: one budget, one actual, one remaining. */
function combine(a: Total | null, b: Total | null): Total {
  if (!a || !b) return (a ?? b)!;
  const budgetUsd = a.budgetUsd === null && b.budgetUsd === null ? null : (a.budgetUsd ?? 0) + (b.budgetUsd ?? 0);
  const otherCostUsd = a.otherCostUsd === undefined && b.otherCostUsd === undefined ? undefined : (a.otherCostUsd ?? 0) + (b.otherCostUsd ?? 0);
  const t = { budgetUsd, committedUsd: a.committedUsd + b.committedUsd, accruedUsd: a.accruedUsd + b.accruedUsd, otherCostUsd };
  return { ...t, remainingUsd: budgetUsd === null ? null : budgetUsd - spendOf(t) - (otherCostUsd ?? 0) };
}
function combinePrevious(a: PreviousFigures | null, b: PreviousFigures | null): PreviousFigures | null {
  if (!a || !b) return a ?? b;
  return { budgetUsd: a.budgetUsd === null && b.budgetUsd === null ? null : (a.budgetUsd ?? 0) + (b.budgetUsd ?? 0), actualUsd: a.actualUsd + b.actualUsd };
}

/** One vessel of the list: its Sertica sheet, its SEA or PEA budget, or both (SPEC §8.6). */
type Vessel = { pane: Pane; name: string; u: UnitSheet | null; ac: AutoCountVessel | null; total: Total; overCount: number; budgeted: boolean };

/** Budgets (SPEC §8.6). Without budget access it is locked and fetches no data. */
export function BudgetsTab() {
  const { me, freshness } = useDashboard();
  if (!me.canSeeBudgets)
    return (
      <div className="card p-6">
        <div className="font-semibold">🔒 Budgets</div>
        <p className="text-muted mt-1">{LOCKED_MESSAGE}</p>
      </div>
    );
  if (!freshness) return <div className="card h-64 animate-pulse" />;
  if (!freshness.budgets.asOf)
    return <div className="card p-8 text-center text-muted">Budgets have not been synced yet. Spend still shows on the other tabs; nothing is flagged Over budget.</div>;
  return <FollowUpView />;
}

function FollowUpView() {
  const { freshness, filters, search } = useDashboard();
  const [exporting, setExporting] = useState(false);
  const { data: page, isError } = useBudgetsPage();
  const [pane, setPane] = useState<Pane>({ kind: "all" });
  // The vessel list folds away, so the lists below it are in reach.
  const [foldVessels, setFoldVessels] = useState(false);
  // On by default; kept across vessels, so it holds while flicking through sheets.
  const [hideZero, setHideZero] = useState(true);
  const changes = useMemo(() => indexChanges(page?.changes ?? []), [page?.changes]);
  // A Sertica unit's SEA or PEA budget joins its sheet; a vessel with only one of them is listed with it.
  const vessels = useMemo(() => {
    const acs = page?.autoCount.vessels ?? [];
    const acOf = new Map(acs.flatMap((v) => (v.unitNo ? [[v.unitNo, v] as const] : [])));
    const units = page?.followUp.units ?? [];
    const out: Vessel[] = [
      ...units.map((u) => {
        const ac = (u.unitNo && acOf.get(u.unitNo)) || null;
        return { pane: { kind: "unit", unitNo: u.unitNo } as Pane, name: vesselName(u), u, ac, total: combine(vesselTotal(u), ac && sheetFigures(ac)), overCount: u.overCount + (ac?.overCount ?? 0), budgeted: !!u.budgetNo || !!ac };
      }),
      ...acs
        .filter((v) => !v.unitNo || !units.some((u) => u.unitNo === v.unitNo))
        .map((ac) => ({ pane: { kind: "autocount", key: autoCountKey(ac) } as Pane, name: ac.vesselName, u: null, ac, total: sheetFigures(ac), overCount: ac.overCount, budgeted: true })),
    ];
    // Most used first; vessels without a budget after them.
    return out.sort((a, b) => (a.budgeted ? 0 : 1) - (b.budgeted ? 0 : 1) || usedShare(b.total) - usedShare(a.total) || a.name.localeCompare(b.name));
  }, [page?.followUp.units, page?.autoCount.vessels]);
  const changeCount = page?.changes.reduce((n, w) => n + w.changes.length, 0) ?? 0;
  const entries: { pane: Pane; label: string }[] = [
    { pane: { kind: "all" }, label: page?.closed ? "All vessels · year-end result" : "All vessels · needs attention" },
    ...vessels.map((v) => ({ pane: v.pane, label: v.name })),
    { pane: { kind: "projects" }, label: "Projects · all vessels" },
    { pane: { kind: "unbudgeted" }, label: "Unbudgeted spend · all vessels" },
    // A closed year has no budget changes to show (the log starts with the first budget sync).
    ...(page?.closed ? [] : [{ pane: { kind: "changes" } as Pane, label: `Budget changes · ${changeCount}` }]),
  ];
  const lists = entries.filter((x) => x.pane.kind === "projects" || x.pane.kind === "unbudgeted" || x.pane.kind === "changes");
  // A vessel dropped by the Vessel filter, or Budget changes in a closed year, falls back to All vessels.
  const shown = !entries.some((x) => paneKey(x.pane) === paneKey(pane)) ? ({ kind: "all" } as Pane) : pane;
  const shownVessel = vessels.find((v) => paneKey(v.pane) === paneKey(shown));
  const item = (p: Pane) => `w-full text-left rounded px-2 py-1.5 ${paneKey(p) === paneKey(shown) ? "bg-hover ring-1 ring-brand2" : "hover:bg-hover"}`;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <PeriodPicker years={page?.years ?? []} />
        {page && <ClosedYear page={page} />}
        {/* Sertica has no forecasts for a closed year's budgets, so the toggle would change nothing. */}
        {!page?.closed && <ForecastToggle />}
        <div className="flex-1" />
        <BudgetsAsOf f={freshness} />
        <button
          className="input text-[12.5px]"
          disabled={exporting}
          title="Four sheets: Follow-up, PO lines, Projects, Budget changes"
          onClick={async () => {
            setExporting(true);
            try {
              await download(`/budgets/export${qs({ vessel: filters.vessel, period: search.period, forecasts: search.forecasts })}`);
            } finally {
              setExporting(false);
            }
          }}
        >
          {exporting ? "Exporting…" : "Export .xlsx"}
        </button>
      </div>
      {isError && <div className="card p-4 text-[13px] text-[var(--danger)]">Could not load the budget follow-up.</div>}
      {!page ? (
        <div className="card h-64 animate-pulse" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-4 items-start">
          <select
            className="input lg:hidden w-full"
            aria-label="Show"
            value={paneKey(shown)}
            onChange={(e) => setPane(entries.find((x) => paneKey(x.pane) === e.target.value)!.pane)}
          >
            {entries.map((x) => (
              <option key={paneKey(x.pane)} value={paneKey(x.pane)}>
                {x.label}
              </option>
            ))}
          </select>
          <aside className="card p-2 space-y-0.5 hidden lg:block" aria-label="Vessels">
            <button className={item({ kind: "all" })} onClick={() => setPane({ kind: "all" })}>
              <div className="text-[13px] font-semibold">All vessels</div>
              <div className="text-[11px] text-muted">
                {page.yearEnd ? `Year-end result · ${page.yearEnd.over.length} over` : `Needs attention · ${page.attention.filter((r) => r.kind !== "close").length} over`}
              </div>
            </button>
            <button className="label w-full flex items-center gap-1 px-2 pt-2 pb-1 text-left hover:text-ink" aria-expanded={!foldVessels} onClick={() => setFoldVessels(!foldVessels)}>
              <span aria-hidden className="inline-block w-3">
                {foldVessels ? "▸" : "▾"}
              </span>
              <span className="flex-1">Vessels · most used first</span>
              <span className="normal-case font-normal tracking-normal">{vessels.length}</span>
            </button>
            {/* Folded, only the vessel whose sheet is open stays. */}
            {vessels.filter((v) => !foldVessels || v === shownVessel).map((v) => (
              <button key={paneKey(v.pane)} className={item(v.pane)} onClick={() => setPane(v.pane)}>
                <div className="flex items-center gap-1 text-[13px] font-semibold">
                  <span className="flex-1 truncate">{v.name}</span>
                  {/* Where its budget is: Sertica's alone needs no mark. */}
                  {v.ac && <span className="text-[11px] text-muted font-normal" title={v.u?.budgetNo ? `Sertica and ${v.ac.source} budgets added up` : `${v.ac.source} budget only`}>{v.u?.budgetNo ? `+ ${v.ac.source}` : v.ac.source}</span>}
                  <OverCount n={v.overCount} />
                </div>
                {v.budgeted ? (
                  <>
                    <UsedBar budgetUsd={v.total.budgetUsd} spendUsd={spendOf(v.total)} width={190} />
                    {v.u?.status === "Retired" && <div className="text-[11px] text-muted">Retired budget</div>}
                  </>
                ) : (
                  <div className="text-[11px] text-muted">No budget · {usd(spendOf(v.total))} unbudgeted</div>
                )}
              </button>
            ))}
            {!vessels.length && <div className="text-[12px] text-muted px-2 py-1">No budgets or spend for this period.</div>}
            <div className="border-t border-line mt-1 pt-1">
              {lists.map((x) => (
                <button key={paneKey(x.pane)} className={`${item(x.pane)} text-[13px]`} onClick={() => setPane(x.pane)}>
                  {x.label}
                </button>
              ))}
            </div>
          </aside>
          <section className="card p-3 md:p-4 space-y-3 min-w-0">
            {shown.kind === "all" && (page.yearEnd ? <YearEndResult page={page} yearEnd={page.yearEnd} /> : <NeedsAttention page={page} changes={changes} />)}
            {shownVessel && <BudgetSheet key={paneKey(shownVessel.pane)} vessel={shownVessel} page={page} changes={changes} hideZero={hideZero} setHideZero={setHideZero} />}
            {shown.kind === "projects" && <Projects rows={page.followUp.projects} period={page.period} />}
            {shown.kind === "unbudgeted" && <UnbudgetedSpend units={page.followUp.units} period={page.period} />}
            {shown.kind === "changes" && <BudgetChanges page={page} />}
          </section>
        </div>
      )}
    </div>
  );
}

const periodLabel = (p: BudgetsPage["period"]) => (p.quarter === null ? `Year ${p.year}` : `Q${p.quarter} ${p.year}`);

const ATTENTION: Record<AttentionKind, [title: string, note: string]> = {
  over_now: ["Over budget in the current quarter", "Open POs and unordered requisitions on these lines carry the Over budget flag."],
  over_earlier: ["Over budget in an earlier quarter, with POs still open", "The flag stays on the open POs ordered in that quarter."],
  close: ["Close to budget in the current quarter", "85 % used or more. Not flagged."],
};

/** Rows shown per Needs attention section before "Show all". */
const ATTENTION_ROWS = 25;

function NeedsAttention({ page, changes }: { page: BudgetsPage; changes: Map<string, LineChanges> }) {
  const [all, setAll] = useState<Set<AttentionKind>>(new Set());
  const shownOf = (kind: AttentionKind) => page.attention.filter((r) => r.kind === kind).slice(0, all.has(kind) ? undefined : ATTENTION_ROWS);
  // Previous and Next go through every row shown, section by section.
  const steps = (Object.keys(ATTENTION) as AttentionKind[]).flatMap((kind) => shownOf(kind).flatMap((r) => stepOf(attentionRef(r), { year: page.current.year, quarter: r.quarter })));
  return (
    <>
      <div>
        <h3 className="text-[16px] font-bold">Needs attention</h3>
        <p className="text-[12px] text-muted">
          Judged per quarter, as the Over budget flag is, whatever period is picked above · current quarter Q{page.current.quarter} {page.current.year} · {BUDGET_CAPTION}
        </p>
      </div>
      {(Object.keys(ATTENTION) as AttentionKind[]).map((kind) => {
        const rows = page.attention.filter((r) => r.kind === kind);
        const [title, note] = ATTENTION[kind];
        return (
          <section key={kind} aria-label={title}>
            <h4 className="text-[14px] font-bold">
              {title} <span className="text-muted font-normal">· {rows.length}</span>
            </h4>
            <p className="text-[11px] text-muted mb-1">{note}</p>
            <div className="overflow-x-auto">
              <table className="sheet">
                <thead>
                  <tr>
                    <th>Vessel · purchase type · technical account</th>
                    <th>Quarter</th>
                    <th>Budget</th>
                    <th title="Committed + accrued">Actual</th>
                    <th>Remaining</th>
                    <th style={{ textAlign: "left" }}>Used</th>
                  </tr>
                </thead>
                <tbody>
                  {shownOf(kind).map((r) => (
                    <AttentionLine key={`${r.unitNo}|${r.purchaseTypeNo}|${r.technicalAccountNo}|${r.quarter}`} r={r} year={page.current.year} steps={steps} changes={changes.get(changeKey(r.budgetNo, r.purchaseTypeNo, r.technicalAccountNo))} />
                  ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan={6} className="text-muted">
                        None.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {rows.length > ATTENTION_ROWS && !all.has(kind) && (
              <button className="text-[12.5px] text-brand font-semibold mt-1" onClick={() => setAll((s) => new Set([...s, kind]))}>
                Show all {rows.length}
              </button>
            )}
          </section>
        );
      })}
    </>
  );
}

/**
 * A closed year's result (SPEC §8.6), in place of Needs attention: how many lines were over in each
 * quarter, the lines over for the year, and those with the most budget left.
 */
function YearEndResult({ page, yearEnd }: { page: BudgetsPage; yearEnd: NonNullable<BudgetsPage["yearEnd"]> }) {
  const [all, setAll] = useState<Set<string>>(new Set());
  const year = { year: page.period.year, quarter: null };
  const sections = [
    { key: "over", title: "Over budget for the year", note: "Actual past the year's budget. Nothing in a closed year is flagged.", rows: yearEnd.over, last: "Over by" },
    { key: "under", title: "Most budget left", note: "Lines that ended the year under budget, most left first.", rows: yearEnd.underspent, last: "Left" },
  ];
  const shownOf = (s: (typeof sections)[number]) => s.rows.slice(0, all.has(s.key) ? undefined : ATTENTION_ROWS);
  const steps = sections.flatMap((s) => shownOf(s).flatMap((r) => stepOf(yearEndRef(r), year)));
  return (
    <>
      <div>
        <h3 className="text-[16px] font-bold">Year-end result · Year {page.period.year}</h3>
        <p className="text-[12px] text-muted">A closed year: its budgets are Retired in Sertica, and its lines are judged for the whole year · {BUDGET_CAPTION}</p>
      </div>
      <div className="flex flex-wrap gap-2" aria-label="Lines over budget per quarter">
        {yearEnd.quarters.map((q) => (
          <div key={q.quarter} className="border border-line rounded-card px-3 py-1.5 text-[12.5px] tnum">
            <span className="font-semibold">Q{q.quarter}</span> · <span className={q.overCount ? "neg" : "text-muted"}>{q.overCount} over</span>
            <span className="text-muted"> of {q.lineCount}</span>
          </div>
        ))}
      </div>
      {sections.map((s) => (
        <section key={s.key} aria-label={s.title}>
          <h4 className="text-[14px] font-bold">
            {s.title} <span className="text-muted font-normal">· {s.rows.length}</span>
          </h4>
          <p className="text-[11px] text-muted mb-1">{s.note}</p>
          <div className="overflow-x-auto">
            <table className="sheet">
              <thead>
                <tr>
                  <th>Vessel · purchase type · technical account</th>
                  <th>Budget</th>
                  <th title="Committed + accrued">Actual</th>
                  <th>{s.last}</th>
                  <th style={{ textAlign: "left" }}>Used</th>
                </tr>
              </thead>
              <tbody>
                {shownOf(s).map((r) => (
                  <YearEndRow key={`${r.unitNo}|${r.purchaseTypeNo}|${r.technicalAccountNo}`} r={r} period={year} steps={steps} />
                ))}
                {!s.rows.length && (
                  <tr>
                    <td colSpan={5} className="text-muted">
                      None.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {s.rows.length > ATTENTION_ROWS && !all.has(s.key) && (
            <button className="text-[12.5px] text-brand font-semibold mt-1" onClick={() => setAll((x) => new Set([...x, s.key]))}>
              Show all {s.rows.length}
            </button>
          )}
        </section>
      ))}
    </>
  );
}

const yearEndRef = (r: YearEndLine): BudgetRowRef => ({ unitNo: r.unitNo, row: "line", purchaseTypeNo: r.purchaseTypeNo, technicalAccountNo: r.technicalAccountNo });

function YearEndRow({ r, period, steps }: { r: YearEndLine; period: BudgetsPage["period"]; steps: BudgetLineStep[] }) {
  const openLine = useOpenLine();
  const isShown = useShown();
  const go = () => openLine(yearEndRef(r), period, steps);
  return (
    <tr {...opens(go, isShown(yearEndRef(r), period))}>
      <td>
        <button className="open-label" onClick={(e) => (e.stopPropagation(), go())}>
          <span className="font-semibold">{vesselName(r)}</span>
          <span className="text-muted">
            {" "}
            · {r.purchaseTypeNo} · {r.technicalAccountNo}
          </span>
        </button>
      </td>
      <td>{usd(r.budgetUsd)}</td>
      <td>{usd(spendOf(r))}</td>
      <td className={r.remainingUsd < 0 ? "neg" : ""}>{usd(Math.abs(r.remainingUsd))}</td>
      <td style={{ textAlign: "left" }}>
        <UsedBar budgetUsd={r.budgetUsd} spendUsd={spendOf(r)} />
      </td>
    </tr>
  );
}

/**
 * A line whose Actual is within budget but whose Remaining (budget − actual − other cost) is
 * $1.00 or more below zero: its POs under approval do not fit, so they carry the Over budget flag.
 */
const overByOtherCost = (r: { over?: boolean; remainingUsd: number | null }) => !r.over && r.remainingUsd !== null && Math.round(r.remainingUsd * 100) <= -100;
const OTHER_COST_OVER = "Actual is within budget, but POs under approval take it over by $1 or more; those POs carry the Over budget flag";

const attentionRef = (r: AttentionRow): BudgetRowRef => ({ unitNo: r.unitNo, row: "line", purchaseTypeNo: r.purchaseTypeNo, technicalAccountNo: r.technicalAccountNo });

function AttentionLine({ r, year, steps, changes }: { r: AttentionRow; year: number; steps: BudgetLineStep[]; changes?: LineChanges }) {
  const openLine = useOpenLine();
  const isShown = useShown();
  const period = { year, quarter: r.quarter };
  const go = () => openLine(attentionRef(r), period, steps);
  return (
    <tr {...opens(go, isShown(attentionRef(r), period))}>
      <td>
        <span className="inline-flex flex-wrap items-center gap-1">
          <button className="open-label" onClick={(e) => (e.stopPropagation(), go())}>
            <span className="font-semibold">{vesselName(r)}</span>
            <span className="text-muted">
              {" "}
              · {r.purchaseTypeNo} · {r.technicalAccountNo}
            </span>
          </button>
          {overByOtherCost(r) && <OverChip title={OTHER_COST_OVER} />}
          <ChangeMark changes={changes} />
        </span>
      </td>
      <td>Q{r.quarter}</td>
      <td>{usd(r.budgetUsd)}</td>
      <td>{usd(spendOf(r))}</td>
      <td className={r.remainingUsd < 0 ? "neg" : ""}>{usd(r.remainingUsd)}</td>
      <td style={{ textAlign: "left" }}>
        <UsedBar budgetUsd={r.budgetUsd} spendUsd={spendOf(r)} />
      </td>
    </tr>
  );
}

/** A row's money cells; with `prev` given (null: none), the same period a year earlier follows. */
function MoneyCells({ f, unbudgeted = false, prev }: { f: Figures & { remainingUsd?: number | null }; unbudgeted?: boolean; prev?: PreviousFigures | null }) {
  return (
    <>
      <td>{unbudgeted ? "—" : usd(f.budgetUsd)}</td>
      <td>{usd(spendOf(f))}</td>
      <td>{f.otherCostUsd === undefined ? "—" : usd(f.otherCostUsd)}</td>
      <td className={f.remainingUsd != null && f.remainingUsd < 0 ? "neg" : ""}>{unbudgeted ? "—" : usd(f.remainingUsd)}</td>
      <td style={{ textAlign: "left" }}>{unbudgeted ? <span className="text-[11.5px] text-muted">—</span> : <UsedBar budgetUsd={f.budgetUsd} spendUsd={spendOf(f)} />}</td>
      {prev !== undefined && (
        <>
          <td className="prev">{usd(prev?.budgetUsd)}</td>
          <td className={`prev ${prev && prev.budgetUsd !== null && prev.actualUsd - prev.budgetUsd >= 1 ? "neg" : ""}`}>{prev ? usd(prev.actualUsd) : "—"}</td>
        </>
      )}
    </>
  );
}

const SHEET_HEAD = (first: string, previous: string) => (
  <tr>
    <th>{first}</th>
    <th>Budget</th>
    <th title="Committed + accrued">Actual</th>
    <th title="POs under approval (not ordered yet); shown for a period holding the current quarter">Other cost</th>
    <th>Remaining</th>
    <th style={{ textAlign: "left" }}>Used</th>
    <th className="prev" title={`The same line's budget in ${previous}, from that year's budget (Retired once the year closed)`}>
      Budget {previous}
    </th>
    <th className="prev" title={`The same line's actual (committed + accrued) in ${previous}`}>
      Actual {previous}
    </th>
  </tr>
);

/** A row with no budget, no spend and no other cost, to the cent; a $0 budget with spend is over, so it is not zero. */
const isZero = (f: { budgetUsd?: number | null; committedUsd: number; accruedUsd: number; otherCostUsd?: number }) => [f.budgetUsd ?? 0, spendOf(f), f.otherCostUsd ?? 0].every((n) => Math.round(n * 100) === 0);

/** A vessel's SEA or PEA lines on its sheet: a heading row, then each expense group and its cost items. */
function autoCountRows({
  ac,
  lines,
  hideZero,
  open,
  toggle,
  go,
  isShown,
}: {
  ac: AutoCountVessel;
  lines: AutoCountLine[] | undefined;
  hideZero: boolean;
  open: Set<string>;
  toggle: (g: string) => void;
  go: (l: AutoCountLine) => () => void;
  isShown: (l: AutoCountLine) => boolean;
}): { rows: ReactElement[]; hidden: number } {
  const head = (
    <tr key="ac:head" className="source">
      <td colSpan={8} style={{ textAlign: "left" }}>
        <span className="font-semibold">{ac.source} budget</span>
        <span className="text-muted"> · {[ac.database, `project ${ac.projectNo}`, ac.companyName].filter(Boolean).join(" · ")}</span>
      </td>
    </tr>
  );
  if (!lines)
    return {
      rows: [
        head,
        <tr key="ac:loading">
          <td colSpan={8} className="text-muted" style={{ textAlign: "left" }}>
            Loading the {ac.source} budget…
          </td>
        </tr>,
      ],
      hidden: 0,
    };
  const groups = new Map<string, AutoCountLine[]>();
  for (const l of lines) groups.set(l.expenseGroup ?? "", [...(groups.get(l.expenseGroup ?? "") ?? []), l]);
  const rows = [head];
  for (const [g, ls] of groups) {
    const shownLines = ls.filter((l) => !hideZero || !isZeroLine(l));
    if (!shownLines.length) continue;
    const isOpen = open.has(g);
    rows.push(
      <tr key={`ac:g:${g}`} className="parent">
        <td>
          <button className="inline-flex items-center gap-1 text-left" aria-expanded={isOpen} onClick={() => toggle(g)}>
            <span className="inline-block w-4 text-muted" aria-hidden>
              {isOpen ? "▾" : "▸"}
            </span>
            {g || "No expense group"}
          </button>{" "}
          <OverCount n={ls.filter((l) => l.over).length} />
        </td>
        <MoneyCells f={sheetFigures(sumLines(ls))} prev={sumPrevious(ls.map((l) => l.previous))} />
      </tr>,
    );
    if (isOpen)
      for (const l of shownLines)
        rows.push(
          <tr key={`ac:${g}|${l.costItem}|${l.statusKey}`} {...opens(go(l), isShown(l))}>
            <td style={{ paddingLeft: 48 }}>
              <span className="inline-flex flex-wrap items-center gap-1">
                <button className="open-label" onClick={(e) => (e.stopPropagation(), go(l)())}>
                  {l.costItem}
                </button>
                {l.statusName && <span className="text-muted text-[12px]">· {l.statusName}</span>}
                {l.over && <OverChip title={`Over ${ac.source}'s budget by $1 or more; not the Sertica flag`} />}
              </span>
            </td>
            <MoneyCells f={sheetFigures(l)} prev={previousFigures(l.previous)} />
          </tr>,
        );
  }
  return { rows, hidden: hideZero ? lines.filter(isZeroLine).length : 0 };
}

function BudgetSheet({ vessel, page, changes, hideZero, setHideZero }: { vessel: Vessel; page: BudgetsPage; changes: Map<string, LineChanges>; hideZero: boolean; setHideZero: (v: boolean) => void }) {
  const { u: sheet, ac } = vessel;
  const openLine = useOpenLine();
  const isShown = useShown();
  const { drawer, open: openDrawer } = useDashboard();
  const sheetRows = sheet?.rows ?? [];
  const parents = sheetRows.flatMap((r) => (r.kind === "parent" ? [r.purchaseTypeNo] : []));
  // Every purchase type starts collapsed; the sheet remounts per vessel, so each opens that way.
  const [closed, setClosed] = useState<Set<string>>(() => new Set(parents));
  // SEA's or PEA's expense groups load after the sheet, so these are the ones opened; all start collapsed too.
  const [acOpen, setAcOpen] = useState<Set<string>>(new Set());
  const { data: acSheet } = useAutoCountSheet(ac ?? undefined, page.period);
  const acGroups = [...new Set((acSheet?.lines ?? []).map((l) => l.expenseGroup ?? ""))];
  const unitNo = sheet?.unitNo ?? null;
  // The same period a year earlier: null where the line, purchase type or budget did not exist.
  const prevOf = (pt: string | null, ta: string | null) => page.previous.figures[previousKey(unitNo, pt, ta)] ?? null;
  const toggle = (pt: string) => setClosed((s) => (s.has(pt) ? new Set([...s].filter((x) => x !== pt)) : new Set([...s, pt])));
  // Previous and Next go through the sheet's rows in order, the hidden zero rows left out; the
  // purchase type of the line shown opens, so its highlight is in view.
  const steps = sheetRows.flatMap((r) => (r.kind === "parent" || (hideZero && isZero(r)) ? [] : stepOf(rowRef(unitNo, r), page.period)));
  const shownRow = sheetRows.find((r) => isShown(rowRef(unitNo, r), page.period));
  const shownParent = shownRow && (shownRow.kind === "line" || (shownRow.kind === "unbudgeted" && shownRow.reason === "technical_account_not_budgeted")) ? shownRow.purchaseTypeNo : null;
  useEffect(() => {
    if (shownParent && closed.has(shownParent)) setClosed((s) => new Set([...s].filter((x) => x !== shownParent)));
    // Only when the line shown changes, so the user can still fold its purchase type.
  }, [shownParent, drawer]);
  // The SEA or PEA line drawer likewise: its own steps, its expense group opened.
  const at = formatBudgetPeriod(page.period);
  const acRef = (l: AutoCountLine): AutoCountLineRef => ({ source: ac!.source, database: ac!.database, projectNo: ac!.projectNo, costItem: l.costItem, statusKey: l.statusKey });
  const acSteps = acGroups.flatMap((g) => (acSheet?.lines ?? []).filter((l) => (l.expenseGroup ?? "") === g && (!hideZero || !isZeroLine(l))).map(acRef));
  const acShown = (l: AutoCountLine) => drawer?.type === "autocount-line" && drawer.period === at && sameAutoCountLine(drawer.line, acRef(l));
  const acShownGroup = acSheet?.lines.find(acShown)?.expenseGroup;
  useEffect(() => {
    if (acShownGroup !== undefined) setAcOpen((s) => (s.has(acShownGroup ?? "") ? s : new Set([...s, acShownGroup ?? ""])));
  }, [acShownGroup, drawer]);
  const allClosed = (parents.length > 0 || acGroups.length > 0) && parents.every((pt) => closed.has(pt)) && acOpen.size === 0;
  // With zero rows hidden, a purchase type goes too when every row under it is zero.
  const live = new Set<string>();
  let under: string | null = null;
  for (const r of sheetRows) {
    if (r.kind === "parent") under = r.purchaseTypeNo;
    else if (r.kind === "line" || r.reason === "technical_account_not_budgeted") {
      if (under !== null && !isZero(r)) live.add(under);
    } else under = null;
  }
  let hidden = 0;
  // Rows come in sheet order: each parent, then its lines and unbudgeted accounts; then the vessel's own unbudgeted rows.
  let parent: string | null = null;
  const body: ReactElement[] = [];
  for (const [i, r] of sheetRows.entries()) {
    if (r.kind === "parent") {
      parent = r.purchaseTypeNo;
      if (hideZero && !live.has(r.purchaseTypeNo)) continue;
      const open = !closed.has(r.purchaseTypeNo);
      body.push(
        <tr key={i} className="parent">
          <td>
            <button className="inline-flex items-center gap-1 text-left" aria-expanded={open} onClick={() => toggle(r.purchaseTypeNo)}>
              <span className="inline-block w-4 text-muted" aria-hidden>
                {open ? "▾" : "▸"}
              </span>
              {r.purchaseTypeNo}
            </button>{" "}
            <OverCount n={r.overCount} />
          </td>
          <MoneyCells f={r} prev={prevOf(r.purchaseTypeNo, null)} />
        </tr>,
      );
      continue;
    }
    const isUnder = r.kind === "line" || r.reason === "technical_account_not_budgeted";
    if (!isUnder) parent = null;
    if (hideZero && isZero(r)) {
      hidden++;
      continue;
    }
    if (isUnder && parent !== null && closed.has(parent)) continue;
    body.push(
      <SheetLine
        key={i}
        r={r}
        depth={isUnder ? 1 : 0}
        go={() => openLine(rowRef(unitNo, r), page.period, steps)}
        shown={isShown(rowRef(unitNo, r), page.period)}
        prev={r.kind === "line" ? prevOf(r.purchaseTypeNo, r.technicalAccountNo) : null}
        changes={r.kind === "line" ? changes.get(changeKey(sheet!.budgetNo, r.purchaseTypeNo, r.technicalAccountNo)) : undefined}
      />,
    );
  }
  const acPart = ac
    ? autoCountRows({
        ac,
        lines: acSheet?.lines,
        hideZero,
        open: acOpen,
        toggle: (g) => setAcOpen((s) => (s.has(g) ? new Set([...s].filter((x) => x !== g)) : new Set([...s, g]))),
        go: (l) => () => openDrawer({ type: "autocount-line", line: acRef(l), period: at, steps: acSteps }),
        isShown: acShown,
      })
    : null;
  hidden += acPart?.hidden ?? 0;
  const serticaTotal = sheet ? vesselTotal(sheet) : null;
  const serticaPrev = sheet ? prevOf(null, null) : null;
  const acPrev = ac ? previousFigures(ac.previous) : null;
  const both = !!sheet && !!ac;
  const meta = [
    sheet && (sheet.budgetNo ? [sheet.budgetNo, sheet.status, `Year ${page.period.year}`, sheet.companyName].filter(Boolean).join(" · ") : `No Sertica budget for Year ${page.period.year}`),
    ac && `${ac.source} ${ac.database} · project ${ac.projectNo}${sheet ? "" : ` · Year ${page.period.year} · ${ac.companyName ?? ""}`}`,
  ].filter(Boolean);
  return (
    <>
      <div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-[16px] font-bold">{vessel.name}</h3>
          <span className="text-[12px] text-muted">{meta.join(" + ")}</span>
          {sheet?.ambiguous && (
            <span className="badge" style={{ "--c": "var(--budget-warn)" } as CSSProperties} title="Two budgets qualify; the latest updated is used">
              Ambiguous budget
            </span>
          )}
          <span className="ml-auto inline-flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-1.5 text-[12.5px]" title="Hide lines with no budget, no actual and no other cost in the period, and SEA's placeholder lines (1.00 of budget, nothing else)">
              <input type="checkbox" checked={hideZero} onChange={() => setHideZero(!hideZero)} />
              Hide zero lines
            </label>
            {(parents.length > 0 || acGroups.length > 0) && (
              <button
                className="input text-[12.5px]"
                onClick={() => {
                  setClosed(allClosed ? new Set() : new Set(parents));
                  setAcOpen(allClosed ? new Set(acGroups) : new Set());
                }}
              >
                {allClosed ? "Expand all" : "Collapse all"}
              </button>
            )}
          </span>
        </div>
        <p className="text-[11.5px] text-muted">
          {periodLabel(page.period)} ·{" "}
          {!sheet
            ? ""
            : sheet.status === "Retired" && sheet.forecast
            ? `Figures from forecast ${sheet.forecast.forecastNo}${sheet.forecast.forecastMonth ? ` (${sheet.forecast.forecastMonth})` : ""}, the budget's last forecast of the year, as in Sertica's Forecast tab. `
            : sheet.status === "Retired"
            ? "Budget as approved: Sertica holds no forecast of this Retired budget. "
            : sheet.forecast
            ? `Figures from forecast ${sheet.forecast.forecastNo}${sheet.forecast.forecastMonth ? ` (${sheet.forecast.forecastMonth})` : ""}, as in Sertica with Use Forecasts. `
            : sheet.quarterRule === "january"
            ? "This budget holds the whole year in January, so Q1 has all of it and later quarters have none, as in Sertica. "
            : sheet.quarterRule === "per_quarter"
              ? "This budget is entered per quarter: each quarter takes its own months. "
              : "All Sertica spend on this vessel is unbudgeted. "}
          {ac &&
            `${ac.source} rows as its budget inquiry has them: Budget is its total budget less transfers out, Actual its POs by submit date, Other cost its reserve (POs not transferred yet). `}
          · {BUDGET_CAPTION}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="sheet">
          <thead>{SHEET_HEAD("Purchase type / technical account", periodLabel(page.previous.period))}</thead>
          <tbody>
            {both && (
              <tr className="source">
                <td colSpan={8} style={{ textAlign: "left" }}>
                  <span className="font-semibold">Sertica budget</span>
                  {sheet.budgetNo && <span className="text-muted"> · {sheet.budgetNo}</span>}
                </td>
              </tr>
            )}
            {body}
            {acPart?.rows}
            {both && (
              <>
                <tr className="parent subtotal">
                  <td>
                    Sertica subtotal <OverCount n={sheet.overCount} />
                  </td>
                  <MoneyCells f={serticaTotal!} unbudgeted={!sheet.budgetNo} prev={serticaPrev} />
                </tr>
                <tr className="parent subtotal">
                  <td>
                    {ac.source} subtotal <OverCount n={ac.overCount} />
                  </td>
                  <MoneyCells f={sheetFigures(ac)} prev={acPrev} />
                </tr>
              </>
            )}
            <tr className="total">
              <td>
                {vessel.name} total <OverCount n={vessel.overCount} />
              </td>
              <MoneyCells f={vessel.total} unbudgeted={!vessel.budgeted} prev={combinePrevious(serticaPrev, acPrev)} />
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted">
        Purchase-type and expense-group rows are subtotals of their lines, unbudgeted rows included. Unbudgeted rows are never flagged{ac ? `, and ${ac.source}'s lines never set the Over budget flag` : ""}.
        {both && ` The total adds Sertica's and ${ac.source}'s budgets for the vessel; their lines are not matched to each other.`}
        {hidden > 0 && ` ${hidden} zero ${hidden === 1 ? "line" : "lines"} hidden.`}
      </p>
      {sheet?.budgetNo && <SerticaPath budgetNo={sheet.budgetNo} />}
    </>
  );
}

function SheetLine({ r, depth, go, shown, changes, prev }: { r: Exclude<BudgetRow, { kind: "parent" }>; depth: number; go: () => void; shown: boolean; changes?: LineChanges; prev: PreviousFigures | null }) {
  const pad = { paddingLeft: 8 + depth * 20 + 20 };
  const label = (text: string) => (
    <button className="open-label" onClick={(e) => (e.stopPropagation(), go())}>
      {text}
    </button>
  );
  if (r.kind === "line")
    return (
      <tr {...opens(go, shown)}>
        <td style={pad}>
          <span className="inline-flex flex-wrap items-center gap-1">
            {label(r.technicalAccountNo)}
            {r.over ? <OverChip /> : overByOtherCost(r) && <OverChip title={OTHER_COST_OVER} />}
            <ChangeMark changes={changes} />
          </span>
        </td>
        <MoneyCells f={r} prev={prev} />
      </tr>
    );
  const what = r.reason === "technical_account_not_budgeted" ? r.technicalAccountNo : r.reason === "purchase_type_not_budgeted" ? r.purchaseTypeNo : null;
  return (
    <tr {...opens(go, shown, "unbudgeted")}>
      <td style={pad}>{label(`${UNBUDGETED_LABEL[r.reason]}${what ? `: ${what}` : ""}`)}</td>
      <MoneyCells f={{ ...r, budgetUsd: null }} unbudgeted prev={null} />
    </tr>
  );
}

function Projects({ rows, period }: { rows: ProjectRow[]; period: BudgetsPage["period"] }) {
  const { open, drawer } = useDashboard();
  const projects = new Map<string, ProjectRow[]>();
  for (const r of rows) projects.set(r.projectNo ?? "", [...(projects.get(r.projectNo ?? "") ?? []), r]);
  const sorted = [...projects.entries()].sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
  const byCoding = (a: ProjectRow, b: ProjectRow) =>
    (a.purchaseTypeNo ?? "").localeCompare(b.purchaseTypeNo ?? "") ||
    (a.technicalAccountNo ?? "").localeCompare(b.technicalAccountNo ?? "") ||
    Number(b.projectSpend) - Number(a.projectSpend);
  // Other cost (POs under approval) applies only in a period holding the current quarter; elsewhere "—", as on the sheets.
  const withOther = rows.some((r) => r.otherCostUsd !== undefined);
  // Project spend, on unit budgets, both, and other cost.
  const split = (rs: ProjectRow[]) => {
    const own = rs.reduce((n, r) => n + (r.projectSpend ? spendOf(r) : 0), 0);
    const all = rs.reduce((n, r) => n + spendOf(r), 0);
    const other = rs.reduce((n, r) => n + (r.otherCostUsd ?? 0), 0);
    return [own, all - own, all, other] as const;
  };
  // A project row opens all its POs, a coding row its own (SPEC §8.6); Previous and Next go through both, in the order shown.
  const at = formatBudgetPeriod(period);
  const refOf = (r: ProjectRow): ProjectRef => ({ projectNo: r.projectNo, purchaseTypeNo: r.purchaseTypeNo, technicalAccountNo: r.technicalAccountNo, projectSpend: r.projectSpend });
  const groups = sorted.map(([no, rs]) => ({ no, rs: [...rs].sort(byCoding), ref: { projectNo: rs[0]!.projectNo } as ProjectRef }));
  const steps: ProjectStep[] = groups.flatMap((g) => [g.ref, ...g.rs.map(refOf)].map((ref) => ({ ref, period: at })));
  const go = (ref: ProjectRef) => () => open({ type: "project", ref, period: at, steps });
  const isShown = (ref: ProjectRef) => drawer?.type === "project" && drawer.period === at && sameProject(drawer.ref, ref);
  const cells = ([own, onBudget, all, other]: readonly [number, number, number, number]) => (
    <>
      <td>{own ? usd(own) : "—"}</td>
      <td>{onBudget ? usd(onBudget) : "—"}</td>
      <td>{usd(all)}</td>
      <td>{withOther && other ? usd(other) : "—"}</td>
    </>
  );
  return (
    <>
      <div>
        <h3 className="text-[16px] font-bold">Projects</h3>
        <p className="text-[12px] text-muted">
          Everything coded to a project, whatever its purchase type, by purchase type and technical account. <b>Project spend</b> is the project-funded purchase types (admin setting), charged to no
          vessel budget; <b>On unit budgets</b> is already counted on a vessel&apos;s budget line. <b>Other cost</b> is POs under approval, in a period holding the current quarter. Sertica has no project budgets, so there is no budget column, and nothing here is flagged ·{" "}
          {BUDGET_CAPTION}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="sheet">
          <thead>
            <tr>
              <th>Project / purchase type · technical account</th>
              <th title="Committed + accrued on project-funded purchase types no unit budget carries">Project spend</th>
              <th title="Committed + accrued already on a vessel's budget line">On unit budgets</th>
              <th title="Committed + accrued">Actual</th>
              <th title="POs under approval: value to be spent once ordered. Only in a period holding the current quarter">Other cost</th>
              <th style={{ textAlign: "left" }}>Vessels</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(({ no, rs, ref }) => (
              <Fragment key={no}>
                <tr {...opens(go(ref), isShown(ref), "parent")}>
                  <td>
                    <button className="open-label" onClick={(e) => (e.stopPropagation(), go(ref)())}>
                      {no ? `${no}${rs[0]!.projectName ? ` · ${rs[0]!.projectName}` : ""}` : "No project"}
                    </button>
                  </td>
                  {cells(split(rs))}
                  <td />
                </tr>
                {rs.map((r) => (
                  <tr key={`${r.purchaseTypeNo ?? ""}|${r.technicalAccountNo ?? ""}|${r.projectSpend}`} {...opens(go(refOf(r)), isShown(refOf(r)))}>
                    <td style={{ paddingLeft: 28 }}>
                      <button className="open-label" onClick={(e) => (e.stopPropagation(), go(refOf(r))())}>
                        {r.purchaseTypeNo ?? "No purchase type"} · {r.technicalAccountNo ?? "No technical account"}
                      </button>
                    </td>
                    {cells(split([r]))}
                    <td style={{ textAlign: "left", whiteSpace: "normal" }}>{r.unitNos.join(", ")}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
            {rows.length > 0 && (
              <tr className="parent">
                <td>Total</td>
                {cells(split(rows))}
                <td />
              </tr>
            )}
            {!rows.length && (
              <tr>
                <td colSpan={6} className="text-muted">
                  No project spend or spend coded to a project in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UnbudgetedSpend({ units, period }: { units: UnitSheet[]; period: BudgetsPage["period"] }) {
  const openLine = useOpenLine();
  const isShown = useShown();
  const rows = units.flatMap((u) => u.rows.flatMap((r) => (r.kind === "unbudgeted" ? [{ u, r }] : [])));
  const byReason = (reason: UnbudgetedReason) => rows.filter((x) => x.r.reason === reason).sort((a, b) => spendOf(b.r) - spendOf(a.r));
  // Previous and Next go through the rows in the order shown.
  const steps = (Object.keys(UNBUDGETED_LABEL) as UnbudgetedReason[]).flatMap((reason) => byReason(reason).flatMap(({ u, r }) => stepOf(rowRef(u.unitNo, r), period)));
  return (
    <>
      <div>
        <h3 className="text-[16px] font-bold">Unbudgeted spend</h3>
        <p className="text-[12px] text-muted">Spend that matches no budget line, by reason. Each row also shows on its vessel's sheet. Never flagged · {BUDGET_CAPTION}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="sheet">
          <thead>
            <tr>
              <th>Reason / vessel</th>
              <th title="Committed + accrued">Actual</th>
            </tr>
          </thead>
          <tbody>
            {(Object.keys(UNBUDGETED_LABEL) as UnbudgetedReason[]).map((reason) => {
              const rs = byReason(reason);
              if (!rs.length) return null;
              const t = rs.reduce((n, x) => n + spendOf(x.r), 0);
              return (
                <Fragment key={reason}>
                  <tr className="parent">
                    <td>
                      {UNBUDGETED_LABEL[reason]} <span className="text-muted font-normal">· {rs.length}</span>
                    </td>
                    <td>{usd(t)}</td>
                  </tr>
                  {rs.map(({ u, r }, i) => {
                    const go = () => openLine(rowRef(u.unitNo, r), period, steps);
                    return (
                      <tr key={i} {...opens(go, isShown(rowRef(u.unitNo, r), period))}>
                        <td style={{ paddingLeft: 28 }}>
                          <button className="open-label" onClick={(e) => (e.stopPropagation(), go())}>
                            {vesselName(u)}
                            {(r.purchaseTypeNo || r.technicalAccountNo) && <span className="text-muted"> · {[r.purchaseTypeNo, r.technicalAccountNo].filter(Boolean).join(" · ")}</span>}
                          </button>
                        </td>
                        <td>{usd(spendOf(r))}</td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
            {!rows.length && (
              <tr>
                <td colSpan={2} className="text-muted">
                  No unbudgeted spend in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

const CHANGE_REASON: Record<string, string> = { edited: "", added: "added", removed: "removed", budget_deleted: "budget deleted" };

function BudgetChanges({ page }: { page: BudgetsPage }) {
  return (
    <>
      <div>
        <h3 className="text-[16px] font-bold">Budget changes</h3>
        <p className="text-[12px] text-muted">
          Year totals of lines that changed in Sertica, in the budgets shown, from the budget sync's change log. Changes are not paired: a transfer is two rows in the same window. {SERTICA_COMMENTS}
        </p>
      </div>
      {page.changes.map((w) => (
        <section key={w.toRunId} className="border border-line rounded-card p-2">
          <div className="text-[12px] text-muted">
            {w.restarted && !w.changes.length ? `Log restarted ${windowLabel(w).replace(/^between/, "after")}: changes are recorded from this sync on` : `Changed ${windowLabel(w)}`}
          </div>
          {w.changes.length > 0 && (
            <div className="overflow-x-auto">
              <table className="sheet">
                <thead>
                  <tr>
                    <th>Vessel · purchase type · technical account</th>
                    <th>Old year total</th>
                    <th>New year total</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {w.changes.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <span className="font-semibold">{vesselName(c)}</span>
                        <span className="text-muted">
                          {" "}
                          · {c.purchaseTypeNo} · {c.technicalAccountNo}
                          {CHANGE_REASON[c.reason] ? ` · ${CHANGE_REASON[c.reason]}` : ""}
                        </span>
                        {w.transferUnits.includes(c.unitNo) && <span className="text-[11px] text-brand"> · looks like a transfer</span>}
                      </td>
                      <td>{usd(c.oldTotalUsd)}</td>
                      <td>{usd(c.newTotalUsd)}</td>
                      <td className={changeAmount(c) < 0 ? "neg" : ""}>{signed(changeAmount(c))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}
      {!page.changes.length && <p className="text-[13px] text-muted">No budget changes recorded yet.</p>}
    </>
  );
}
